import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { type OnboardingLlmRequest, OnboardingLlmRequestSchema } from '@innermate/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

interface Props {
  onAdvance: () => void;
}

export function StepLlm({ onAdvance }: Props) {
  const [form, setForm] = useState({
    name: 'mlx-local',
    baseUrl: 'http://localhost:8183/v1',
    apiKey: 'sk-local',
    model: 'mlx-community/gemma-4-e4b-it-4bit',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: OnboardingLlmRequest) => api.onboardingLlm(body),
    onSuccess: () => onAdvance(),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '알 수 없는 오류');
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    setErrors({});
    const parsed = OnboardingLlmRequestSchema.safeParse(form);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        errs[String(issue.path[0])] = issue.message;
      }
      setErrors(errs);
      return;
    }
    mutation.mutate(parsed.data);
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <Field
        label="별칭"
        htmlFor="name"
        hint="목록에서 구분하기 위한 라벨 (예: mlx-local, openai-prod)"
        error={errors.name}
      >
        <Input
          id="name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </Field>

      <Field
        label="Base URL"
        htmlFor="baseUrl"
        hint="OpenAI 호환 엔드포인트 (예: http://localhost:8183/v1)"
        error={errors.baseUrl}
      >
        <Input
          id="baseUrl"
          value={form.baseUrl}
          onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
        />
      </Field>

      <Field
        label="API 키"
        htmlFor="apiKey"
        hint="로컬 mlx 등 키가 필요 없는 경우 임의 값 입력"
        error={errors.apiKey}
      >
        <Input
          id="apiKey"
          type="password"
          value={form.apiKey}
          onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
          autoComplete="new-password"
        />
      </Field>

      <Field label="모델" htmlFor="model" error={errors.model}>
        <Input
          id="model"
          value={form.model}
          onChange={(e) => setForm({ ...form, model: e.target.value })}
          className="font-mono text-xs"
        />
      </Field>

      {serverError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {serverError}
        </div>
      ) : null}

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? '/v1/models 호출 중…' : '헬스체크 후 저장'}
        </Button>
      </div>
    </form>
  );
}
